import { WalletAdapter, walletError } from '../types'
import { checkNativeWalletSupport, requestNativeWalletPayment } from 'utils/nativeApp'

/**
 * Apple Pay wywoływany z poziomu natywnej aplikacji iOS. Apple Pay JS (applePay.ts) nie działa
 * w WKWebView, więc tu zamiast tego przekazujemy żądanie do appki przez postMessage
 * (RequestWalletPayment) i czekamy na window.onNativeWalletResult - kontrakt opisany w
 * utils/nativeApp.ts (`requestNativeWalletPayment`) i types/global.d.ts (`NativeWalletRequest` /
 * `NativeWalletResult`). Po stronie natywnej (mm_ios) obsługuje to WalletPaymentManager
 * (PKPaymentRequest) + `wallets` w window.__NATIVE_APP__ (WebViewCoordinator.replayNativeState).
 *
 * Merchant ID do PKPaymentRequest NIE jest hardkodowany w appce - appka czyta go z
 * `walletForm.applePay.merchantIdentifier`, które backend wypełnia po wymianie certyfikatu
 * Payment Processing z PayU (§1.4a kontraktu). Brak tego pola = appka nie ma czym otworzyć
 * arkusza, więc metoda jest niedostępna, nawet jeśli appka ogólnie zgłasza obsługę Apple Pay.
 *
 * W przeciwieństwie do Apple Pay JS na web (gdzie completePayment czeka na `onAuthorized` w
 * środku otwartego arkusza), natywny arkusz domyka się od razu po odebraniu tokenu - appka nie
 * czeka na nasze rozliczenie. `onAuthorized` wołamy więc DOPIERO PO otrzymaniu tokenu, nie w
 * trakcie. Wywołujący kod (usePaymentProcessing.payWithWallet) i tak zawsze przekazuje
 * `onAuthorized` dla `applepay` - ten adapter musi ten kontrakt uszanować tak samo jak web-owy.
 */
export const nativeApplePayAdapter: WalletAdapter = {
  async isAvailable(walletForm) {
    return !!walletForm?.applePay?.merchantIdentifier && checkNativeWalletSupport('applepay')
  },

  async requestToken(walletForm, { amount, label, sandbox, onAuthorized }) {
    const token = await requestNativeWalletPayment({
      wallet: 'applepay',
      amount: amount.toFixed(2),
      currencyCode: walletForm.currencyCode,
      countryCode: walletForm.countryCode,
      supportedNetworks: walletForm.supportedNetworks,
      label: walletForm.applePay?.displayName || label,
      merchantIdentifier: walletForm.applePay?.merchantIdentifier ?? null,
      sandbox
    })

    if (onAuthorized) {
      const accepted = await onAuthorized(token)
      if (!accepted) throw walletError('wallet_settlement_failed')
    }

    return token
  }
}
